import { OrgFullDetail, locationLink } from "@garther/interfaces";
import { Box, Button, SelectableCard, Separator, TiptapView } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { Calendar, Globe, Mail, MapPin, Phone, TrendingUp, Users } from "lucide-react";

interface OrgAboutProps {
  org: OrgFullDetail;
}

export function OrgAbout({ org }: OrgAboutProps) {
  const { t } = useI18n();
  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        <Box className="space-y-4" label={t("org.about.title", { orgName: org.name })}>
          <TiptapView content={org.description} variant="normal" />
          {org.mission && (
            <div className="bg-background-highlight rounded-sm p-4">
              <p className="text-responsive-sm font-medium text-text-secondary pb-2">
                {t("org.about.mission")}
              </p>
              <TiptapView content={org.mission as string} variant="compact" />
            </div>
          )}
        </Box>

        {/* <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-6"> */}
        {/*   <Box> */}
        {/*     <p className="text-responsive-base text-text-secondary font-medium"> */}
        {/*       {t("org.performance.totalEvents")} */}
        {/*     </p> */}
        {/*     <p className="text-responsive-md text-text-primary font-normal">{org.stats.totalEvents}</p> */}
        {/*   </Box> */}
        {/*   <Box> */}
        {/*     <p className="text-responsive-base text-text-secondary font-medium"> */}
        {/*       {t("org.performance.avgAttendance")} */}
        {/*     </p> */}
        {/*     <p className="text-responsive-md text-text-primary font-normal">{org.stats.avgAttendance}</p> */}
        {/*   </Box> */}
        {/* </div> */}
      </div>
      {/* Right Column - Sidebar */}
      <div className="space-y-6">
        {/* Stats Card */}
        <Box className="space-y-4" label={t("org.performance.title")}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 px-4">
                <Calendar className="h-5 w-5 text-text-secondary" />
                <div>
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.performance.totalEvents")}
                  </p>
                  <p className="font-normal text-responsive-base">{org.stats.totalEvents}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 px-4">
                <Users className="h-5 w-5 text-text-secondary" />
                <div>
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.performance.avgAttendance")}
                  </p>
                  <p className="font-normal text-responsive-base">{org.stats.avgAttendance}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 px-4">
                <TrendingUp className="h-5 w-5 text-text-secondary" />
                <div>
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.performance.thisMonth")}
                  </p>
                  <p className="font-normal text-responsive-base">
                    {t("org.performance.eventsThisMonth", {
                      count: org.stats.eventsThisMonth
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Box>

        {/* Contact Card */}
        <Box className="space-y-4" label={t("org.contact.title")}>
          <div className="space-y-3">
            {org.contact.email && (
              <SelectableCard
                className="w-full flex items-center gap-3 px-4 py-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(`mailto:${org.contact.email}`, "_blank");
                }}
              >
                <Mail className="h-5 w-5 text-text-secondary" />
                <div className="flex-1 min-w-0">
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.contact.email")}
                  </p>
                  <p className="text-responsive-base font-normal text-text-primary truncate">
                    {org.contact.email}
                  </p>
                </div>
              </SelectableCard>
            )}
            {org.contact.phone && (
              <SelectableCard
                className="w-full flex items-center gap-3 px-4 py-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(`tel:${org.contact.phone}`, "_blank");
                }}
              >
                <Phone className="h-5 w-5 text-text-secondary" />
                <div className="flex-1 min-w-0">
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.contact.phone")}
                  </p>
                  <p className="text-responsive-base font-normal text-text-primary truncate">
                    {org.contact.phone}
                  </p>
                </div>
              </SelectableCard>
            )}
            {org.contact.website && (
              <SelectableCard
                className="w-full flex items-center gap-3 px-4 py-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(`https://${org.contact.website}`, "_blank");
                }}
              >
                <Globe className="h-5 w-5 text-text-secondary" />
                <div className="flex-1 min-w-0">
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.contact.website")}
                  </p>
                  <p className="text-responsive-base font-normal text-text-primary truncate">
                    {org.contact.website}
                  </p>
                </div>
              </SelectableCard>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="text-responsive-sm font-normal text-text-secondary">
              {t("org.contact.socialMedia")}
            </p>
            <div className="flex gap-2">
              {org.contact.instagram && (
                <Button
                  variant="outline"
                  padding="px-2 py-2"
                  className="flex-1 text-responsive-base"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(org.contact.instagram ?? "", "_blank");
                  }}
                >
                  Instagram
                </Button>
              )}
              {org.contact.facebook && (
                <Button
                  variant="outline"
                  padding="px-2 py-2"
                  className="flex-1 text-responsive-base"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(org.contact.facebook ?? "", "_blank");
                  }}
                >
                  Facebook
                </Button>
              )}
            </div>
          </div>
        </Box>

        {/* Location Card */}
        <Box className="space-y-4" label={t("org.location.title")}>
          <div className="space-y-2">
            {org.locations.map((location) => (
              <SelectableCard
                key={location.id}
                className="flex items-center gap-3 hover:bg-background-highlight rounded-sm px-4 py-2"
                onClick={() => {
                  window.open(locationLink(location), "_blank");
                }}
              >
                <MapPin className="h-5 w-5 text-text-secondary" />

                <div className="flex-1">
                  <p className="text-responsive-sm font-medium text-text-secondary">
                    {t("org.location.address")}
                  </p>
                  <p className="text-responsive-base font-normal text-text-primary">{location.name}</p>
                </div>
              </SelectableCard>
            ))}
          </div>
        </Box>
      </div>
    </div>
  );
}
